// src/js/modules/header-menu.js
// Scoped handlers for header / mobile off-canvas menu toggles.
// Idempotent initializer — safe to call multiple times.

import { safeQuery, findAll, createObserverForSelector } from '../helpers/dom.js';

function initHeaderMenu() {
  if (window.__planc_header_initialized) return; window.__planc_header_initialized = true;

  const toggleSel = '#mobile-menu-toggle, .mobile-toggle, .menu-toggle, [data-offcanvas-toggle], .offcanvas-toggle';
  // include theme-specific IDs used in PHP markup
  const offcanvasSel = '#offcanvas-menu, .offcanvas, .site-mobile-menu, [data-offcanvas]';
  const overlaySel = '#offcanvas-menu-overlay, .offcanvas-overlay, .mobile-menu-overlay, [data-offcanvas-overlay]';
  const closeSel = '#offcanvas-close, .offcanvas-close, .menu-close, [data-offcanvas-close]';

  function attach(toggle, offcanvas, overlay) {
    if (!toggle) return;
    if (toggle.__planc_offcanvas_bound) return; toggle.__planc_offcanvas_bound = true;

    function resolveEl(el, sel) {
      // prefer the existing reference when it's still in the document, otherwise query for a fresh one
      try {
        if (el && (document.documentElement || document.body).contains(el)) return el;
      } catch (e) {}
      try { return sel ? safeQuery(sel) : el; } catch (e) { return el; }
    }

    function open() {
      try {
        const _off = resolveEl(offcanvas, offcanvasSel);
        const _ov = resolveEl(overlay, overlaySel);
        if (_off) {
          _off.classList.add('open');
          _off.classList.add('is-open');
          try { _off.classList.remove('-translate-x-full'); } catch (e) {}
          try { _off.classList.add('translate-x-0'); } catch (e) {}
          try { _off.style.transform = 'translateX(0)'; _off.style.zIndex = '1050'; } catch (e) {}
          try { _off.setAttribute('aria-hidden', 'false'); } catch (e) {}
        }
        if (_ov) {
          _ov.classList.add('open');
          _ov.classList.remove('pointer-events-none');
          try { _ov.style.opacity = '1'; _ov.style.pointerEvents = 'auto'; _ov.style.zIndex = '1040'; } catch (e) {}
        }
        document.documentElement.classList.add('offcanvas-open');
      } catch (e) {}
      try { toggle.classList.add('open'); } catch (e) {}
      try { toggle.setAttribute('aria-expanded', 'true'); } catch (e) {}
    }

    function close() {
      try {
        // If focus is inside the offcanvas, move focus back to the toggle before hiding
        try {
          const active = document.activeElement;
          const _offCheck = resolveEl(offcanvas, offcanvasSel);
          if (_offCheck && active && _offCheck.contains(active)) {
            try { toggle.focus({ preventScroll: true }); } catch (e) { try { toggle.focus(); } catch (ee) {} }
          }
        } catch (e) {}

        const _off = resolveEl(offcanvas, offcanvasSel);
        const _ov = resolveEl(overlay, overlaySel);
        if (_off) {
          _off.classList.remove('open');
          _off.classList.remove('is-open');
          try { _off.classList.remove('translate-x-0'); } catch (e) {}
          try { _off.classList.add('-translate-x-full'); } catch (e) {}
          try { _off.style.transform = ''; _off.style.zIndex = ''; } catch (e) {}
          try { _off.setAttribute('aria-hidden', 'true'); } catch (e) {}
        }
        if (_ov) {
          _ov.classList.remove('open');
          try { _ov.style.opacity = ''; _ov.style.pointerEvents = 'none'; _ov.style.zIndex = ''; } catch (e) {}
        }
        document.documentElement.classList.remove('offcanvas-open');
      } catch (e) {}
      try { toggle.classList.remove('open'); } catch (e) {}
      try { toggle.setAttribute('aria-expanded', 'false'); } catch (e) {}
    }

    toggle.addEventListener('click', (e) => { e.preventDefault();
      // toggle visual state if offcanvas absent
      if (toggle.classList.contains('open')) close(); else open();
    });

    // mark as bound for debugging and other code
    try { toggle.dataset.plancBound = '1'; } catch (e) {}
    if (window.PLANC_DEBUG && console && console.debug) console.debug('planc: header toggle bound', toggle);

    // overlay click closes if overlay exists
    if (overlay) {
      overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    }

    // close buttons
    if (offcanvas) {
      const closeBtn = offcanvas.querySelector(closeSel);
      if (closeBtn) closeBtn.addEventListener('click', (e) => { e.preventDefault(); close(); });
    }

    // close on ESC
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && (offcanvas ? offcanvas.classList.contains('is-open') : toggle.classList.contains('open'))) close(); });

    // remember bound toggle so other closing code can toggle visual state
    try { if (offcanvas) offcanvas.__planc_boundToggle = toggle; } catch (e) {}
  }

  const toggles = findAll(toggleSel);
  const offcanvases = findAll(offcanvasSel);
  const overlays = findAll(overlaySel);

  if (toggles.length) {
    if (offcanvases.length) {
      toggles.forEach(t => offcanvases.forEach((o,i) => attach(t, o, overlays[i] || overlays[0] || null)));
    } else {
      // Bind toggles so visual state works even without an offcanvas container present.
      toggles.forEach(t => attach(t, null, null));

      // Observe for offcanvas element insertion, then attach proper handlers
      createObserverForSelector(offcanvasSel, () => {
        const newOffcanvases = findAll(offcanvasSel);
        const newOverlays = findAll(overlaySel);
        toggles.forEach(t => newOffcanvases.forEach((o,i) => attach(t, o, newOverlays[i] || newOverlays[0] || null)));
      }, { timeout: 5000 });
    }
    return;
  }

  // If no toggles present yet, observe for them
  createObserverForSelector(toggleSel, () => initHeaderMenu(), { timeout: 5000 });
}

export { initHeaderMenu };
export default initHeaderMenu;
